import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import currencyData from "assets/json/currency-country-data.json";

interface CurrencyContextProps {
  currencyCode: string;
  currencySymbol: string;
  conversionRate: number;
  allCurrencies: string[];
  setCurrencyCode: (currencyCode: string) => void;
}

const currencySymbolsMap: { [key: string]: string } =
  currencyData.currencies.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.currencyCode]: curr.symbol || curr.currencyCode,
    }),
    {}
  );

const availableCurrencies = currencyData.currencies.map(
  (curr) => curr.currencyCode
);

const CurrencyContext = createContext<CurrencyContextProps | undefined>(
  undefined
);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
};

const fetchCurrencyData = async () => {
  const response = await fetch(
    `https://api.exchangerate-api.com/v4/latest/AUD`
  );
  return response.json();
};

interface CurrencyProviderProps {
  children: ReactNode;
}

const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [currencyCode, setCurrencyCode] = useState<string>("AUD");
  const [currencySymbol, setCurrencySymbol] = useState<string>(
    currencySymbolsMap[currencyCode]
  );
  const [conversionRate, setConversionRate] = useState<number>(1);
  const [allCurrencies, setAllCurrencies] =
    useState<string[]>(availableCurrencies);

  useEffect(() => {
    const getCurrencyData = async () => {
      const cachedData = localStorage.getItem("currencyData");
      const cachedTime = localStorage.getItem("currencyDataTime");

      if (
        cachedData &&
        cachedTime &&
        Date.now() - parseInt(cachedTime) < 24 * 60 * 60 * 1000
      ) {
        const data = JSON.parse(cachedData);
        setAllCurrencies(
          Object.keys(data.rates).filter(
            (currency) => currency in currencySymbolsMap
          )
        );
        setCurrencySymbol(
          currencySymbolsMap[currencyCode as keyof typeof currencySymbolsMap]
        );
        setConversionRate(data.rates[currencyCode]);
      } else {
        const data = await fetchCurrencyData();
        localStorage.setItem("currencyData", JSON.stringify(data));
        localStorage.setItem("currencyDataTime", Date.now().toString());

        const filteredCurrencies = Object.keys(data.rates).filter(
          (currency) => currency in currencySymbolsMap
        );
        setAllCurrencies(filteredCurrencies);
        setCurrencySymbol(
          currencySymbolsMap[currencyCode as keyof typeof currencySymbolsMap]
        );
        setConversionRate(data.rates[currencyCode]);
      }
    };

    getCurrencyData();
  }, [currencyCode]);

  return (
    <CurrencyContext.Provider
      value={{
        currencyCode,
        currencySymbol,
        conversionRate,
        allCurrencies,
        setCurrencyCode,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyProvider;
